import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/AuthContext";
import { Globe, Save, Plus, Trash2, ExternalLink, Copy, Calendar, Mail, Phone, CheckCircle2, X } from "lucide-react";

export default function ChefMiniSite() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [open, setOpen] = useState(false);
  const [offering, setOffering] = useState({ label: "", description: "", prix: 0, duree_min: 30 });

  const load = async () => {
    try {
      const [c, b] = await Promise.all([api.get("/chef/minisite"), api.get("/chef/minisite/bookings")]);
      setConfig(c.data);
      setBookings(b.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/chef/minisite", config); toast.success("Mini-site enregistré"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const addOffering = (e) => {
    e.preventDefault();
    if (!offering.label) return;
    setConfig({ ...config, offerings: [...(config.offerings || []), { ...offering, prix: parseFloat(offering.prix) || 0, duree_min: parseInt(offering.duree_min) || null }] });
    setOffering({ label: "", description: "", prix: 0, duree_min: 30 });
    setOpen(false);
  };

  const removeOffering = (i) => {
    setConfig({ ...config, offerings: config.offerings.filter((_, idx) => idx !== i) });
  };

  const setBookingStatus = async (bid, status) => {
    try { await api.patch(`/chef/minisite/bookings/${bid}?status=${status}`); toast.success("Mis à jour"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeBooking = async (bid) => {
    if (!window.confirm("Supprimer ce booking ?")) return;
    try { await api.delete(`/chef/minisite/bookings/${bid}`); toast.success("Supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!config) return <div className="p-10 text-sm">Chargement…</div>;

  const publicUrl = `${window.location.origin}/sites/${user?.entreprise?.code}`;

  const copyUrl = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("URL copiée");
  };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Mini-site"
        title="Mini-site public"
        sub="Activez votre page publique pour recevoir des réservations et paiements en ligne."
        right={
          <div className="flex gap-2">
            <Button variant="outline" onClick={copyUrl} data-testid="copy-url-btn" className="h-11"><Copy className="h-4 w-4 mr-2" /> Copier URL</Button>
            <a href={publicUrl} target="_blank" rel="noreferrer">
              <Button variant="outline" data-testid="preview-url-btn" className="h-11"><ExternalLink className="h-4 w-4 mr-2" /> Aperçu</Button>
            </a>
            <Button onClick={save} data-testid="save-minisite-btn" className="h-11 bg-[#002FA7] text-white"><Save className="h-4 w-4 mr-2" /> Enregistrer</Button>
          </div>
        }
      />
      <div className="p-6 lg:p-10 grid lg:grid-cols-2 gap-6">
        {/* Config */}
        <div className="space-y-4">
          <div className="bg-white border border-[#DEE2E6] p-5" data-testid="config-section">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2"><Globe className="h-4 w-4 text-[#002FA7]" /> Configuration</div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={config.enabled} onChange={(e) => setConfig({ ...config, enabled: e.target.checked })} data-testid="enabled-toggle" className="h-4 w-4 accent-[#002FA7]" />
                <span className="text-sm font-bold">{config.enabled ? "Activé" : "Désactivé"}</span>
              </label>
            </div>

            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">URL publique</Label>
                <Input value={publicUrl} readOnly className="h-11 mt-1 font-mono text-xs bg-[#F8F9FA]" data-testid="public-url" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Texte d'introduction</Label>
                <Textarea value={config.intro || ""} onChange={(e) => setConfig({ ...config, intro: e.target.value })} rows={3} className="mt-1" data-testid="intro" placeholder="Présentation, services, ambiance…" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Couleur accent</Label>
                  <div className="flex gap-2 mt-1">
                    <Input type="color" value={config.color || "#002FA7"} onChange={(e) => setConfig({ ...config, color: e.target.value })} className="h-11 w-16 cursor-pointer" data-testid="color" />
                    <Input value={config.color || "#002FA7"} onChange={(e) => setConfig({ ...config, color: e.target.value })} className="h-11 font-mono" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Photo URL (optionnel)</Label>
                  <Input value={config.photo_url || ""} onChange={(e) => setConfig({ ...config, photo_url: e.target.value })} className="h-11 mt-1" data-testid="photo-url" placeholder="https://…" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Email contact</Label>
                  <Input type="email" value={config.contact_email || ""} onChange={(e) => setConfig({ ...config, contact_email: e.target.value })} className="h-11 mt-1" data-testid="contact-email" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Téléphone</Label>
                  <Input value={config.contact_telephone || ""} onChange={(e) => setConfig({ ...config, contact_telephone: e.target.value })} className="h-11 mt-1" data-testid="contact-telephone" />
                </div>
              </div>
            </div>
          </div>

          {/* Offerings */}
          <div className="bg-white border border-[#DEE2E6] p-5" data-testid="offerings-section">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.25em] font-bold">Prestations affichées</div>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="offering-add-btn" className="h-9 bg-[#002FA7] text-white"><Plus className="h-3 w-3 mr-1" /> Ajouter</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">Nouvelle prestation</DialogTitle></DialogHeader>
                  <form onSubmit={addOffering} className="space-y-3" data-testid="offering-form">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Nom de la prestation *</Label>
                      <Input required value={offering.label} onChange={(e) => setOffering({ ...offering, label: e.target.value })} className="h-11 mt-1" data-testid="offering-label" />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Description (optionnel)</Label>
                      <Textarea value={offering.description} onChange={(e) => setOffering({ ...offering, description: e.target.value })} rows={2} className="mt-1" data-testid="offering-desc" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Prix (€) — 0 = gratuit</Label>
                        <Input type="number" step="0.01" value={offering.prix} onChange={(e) => setOffering({ ...offering, prix: e.target.value })} className="h-11 mt-1" data-testid="offering-prix" />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Durée (min)</Label>
                        <Input type="number" value={offering.duree_min} onChange={(e) => setOffering({ ...offering, duree_min: e.target.value })} className="h-11 mt-1" data-testid="offering-duree" />
                      </div>
                    </div>
                    <DialogFooter><Button type="submit" className="bg-[#002FA7] text-white" data-testid="offering-submit">Ajouter</Button></DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
            {(!config.offerings || config.offerings.length === 0) ? (
              <div className="text-sm text-[#868E96] italic">Aucune prestation. Ajoutez-en pour les afficher sur votre mini-site.</div>
            ) : (
              <div className="space-y-2">
                {config.offerings.map((o, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center p-3 bg-[#F8F9FA] border border-[#DEE2E6]" data-testid={`offering-row-${i}`}>
                    <div className="col-span-5">
                      <div className="font-bold">{o.label}</div>
                      {o.description && <div className="text-[10px] text-[#868E96]">{o.description}</div>}
                    </div>
                    <div className="col-span-3 text-xs text-[#495057]">{o.duree_min ? `${o.duree_min} min` : "—"}</div>
                    <div className="col-span-3 font-mono font-bold">{o.prix > 0 ? `${o.prix.toFixed(2)} €` : "Gratuit"}</div>
                    <div className="col-span-1 flex justify-end">
                      <Button size="sm" variant="outline" onClick={() => removeOffering(i)} className="h-7 px-2 border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bookings */}
        <div className="bg-white border border-[#DEE2E6]" data-testid="bookings-section">
          <div className="p-5 border-b border-[#DEE2E6] text-xs uppercase tracking-[0.25em] font-bold flex items-center gap-2">
            <Calendar className="h-4 w-4 text-[#002FA7]" /> Demandes reçues ({bookings.length})
          </div>
          {bookings.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">Aucune demande pour le moment.</div>
          ) : bookings.map((b) => (
            <div key={b.id} className="p-4 border-b border-[#F8F9FA] last:border-b-0" data-testid={`booking-${b.id}`}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-bold">{b.client_nom}</div>
                  {b.client_email && <div className="text-xs text-[#495057] flex items-center gap-1 mt-1"><Mail className="h-3 w-3" /> {b.client_email}</div>}
                  {b.client_telephone && <div className="text-xs text-[#495057] flex items-center gap-1"><Phone className="h-3 w-3" /> {b.client_telephone}</div>}
                </div>
                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5" style={{
                  background: b.status === "confirmed" ? "#ECFDF5" : b.status === "rejected" ? "#FEF2F2" : "#FFFBEB",
                  color: b.status === "confirmed" ? "#059669" : b.status === "rejected" ? "#E60000" : "#D97706",
                }}>{b.status}</span>
              </div>
              {b.offering_label && <div className="mt-2 text-xs"><span className="font-semibold">Prestation :</span> {b.offering_label}</div>}
              {b.date_souhaitee && <div className="text-xs"><span className="font-semibold">Date souhaitée :</span> {new Date(b.date_souhaitee).toLocaleString("fr-FR")}</div>}
              {b.message && <div className="mt-2 text-xs text-[#495057] italic">"{b.message}"</div>}
              <div className="mt-3 flex gap-1 flex-wrap">
                {b.status !== "confirmed" && <Button size="sm" onClick={() => setBookingStatus(b.id, "confirmed")} data-testid={`booking-confirm-${b.id}`} className="h-7 bg-[#059669] text-white"><CheckCircle2 className="h-3 w-3 mr-1" /> Confirmer</Button>}
                {b.status !== "rejected" && <Button size="sm" variant="outline" onClick={() => setBookingStatus(b.id, "rejected")} data-testid={`booking-reject-${b.id}`} className="h-7 border-[#E60000] text-[#E60000]"><X className="h-3 w-3 mr-1" /> Refuser</Button>}
                <Button size="sm" variant="outline" onClick={() => removeBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="h-7"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
